/**
 * Popper Payload Helpers
 *
 * Pure stateless decode/encode logic for the popper button editor.
 * Extracts magic-string handling and config transformation from popper.vue
 * into testable pure functions.
 */

// ─── Constants ───────────────────────────────────────────────────────────────

export const LIVECHAT_PAYLOAD = '7@1k704um@9'
export const AGENT_PREFIX = '7@1k704um@9 ___'
export const BOT_PREFIX = 'bot__'
export const OPT_IN_PAYLOAD = 'compliance_opt_in'
export const OPT_OUT_PAYLOAD = 'compliance_opt_out'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PopperConfig {
  text?: string
  action?: string
  payload?: string
  extrainfo?: Record<string, any>
  type?: string
  url_type?: string
  description?: string
}

export interface Bot {
  id: string
  displayname: string
  active?: boolean
}

export interface DecodedPopperState {
  action: string | undefined
  payload: string | undefined
  website: string
  number: string | undefined
  openurlType: 'browser' | 'webview'
  displayMode: string
  accessibilityDescription: string
  superAction: string | undefined
  urlType: 'static' | 'dynamic'
  locationName: string | undefined
  locationAddress: string | undefined
  lat: string | undefined
  lng: string | undefined
  eventTitle: string
  eventDescription: string
  calendarStart: string | undefined
  calendarEnd: string | undefined
  text: string
  descriptionText: string
  suggestionButtonType: string
}

export interface EncodeInput {
  action: string | undefined
  payload: string | undefined
  text: string
  website: string
  number: string | undefined
  openurlType: 'browser' | 'webview'
  displayMode: string
  accessibilityDescription: string
  urlType: 'static' | 'dynamic'
  locationName: string | undefined
  locationAddress: string | undefined
  lat: string | undefined
  lng: string | undefined
  eventTitle: string
  eventDescription: string
  calendarStart: string | undefined
  calendarEnd: string | undefined
  descriptionText: string
  description: boolean
  showSuggestionReplies: boolean
  suggestionButtonType: string
  configExtrainfo?: Record<string, any>
  validateUrl: (url: string) => boolean
  validatePhoneNumber: (number: string | undefined) => true | string
}

export type EncodeResult =
  | {
      legacy: Record<string, any>
      new: Record<string, any>
    }
  | { error: string }

// ─── Decode ──────────────────────────────────────────────────────────────────

/**
 * Decodes a persisted popper config into normalized component state.
 * This is the pure logic extracted from `popper.vue`'s `init()` method
 * (specifically the `switch(this.config.type)` + default if/else chain).
 */
export function decodePopperConfig(config: PopperConfig | undefined, allBots: Bot[]): DecodedPopperState {
  const state: DecodedPopperState = {
    action: undefined,
    payload: undefined,
    website: '',
    number: undefined,
    openurlType: 'browser',
    displayMode: 'full',
    accessibilityDescription: '',
    superAction: undefined,
    urlType: 'static',
    locationName: undefined,
    locationAddress: undefined,
    lat: undefined,
    lng: undefined,
    eventTitle: '',
    eventDescription: '',
    calendarStart: undefined,
    calendarEnd: undefined,
    text: '',
    descriptionText: '',
    suggestionButtonType: 'suggestedReply',
  }

  if (!config) return state

  // Basic field extraction
  if (config.text) state.text = config.text
  if (config.action) state.action = config.action === 'phone' ? 'phone_number' : config.action
  if (config.payload) state.payload = config.payload
  if (config.url_type) state.urlType = config.url_type as 'static' | 'dynamic'
  if (config.description) state.descriptionText = config.description

  // Type-specific decoding
  switch (config.type) {
    case 'phone':
    case 'phone_number': {
      state.action = 'phone_number'
      state.number = state.payload
      break
    }
    case 'share': {
      state.action = 'share'
      break
    }
    case 'website':
    case 'webview': {
      state.action = 'website'
      state.website = state.payload || ''
      state.openurlType = 'browser'
      state.displayMode = 'full'
      state.accessibilityDescription = ''
      break
    }
    case 'app_webview': {
      state.action = 'website' // Normalize to 'website' for dropdown display
      state.website = state.payload || ''
      state.openurlType = 'webview'
      if (config.extrainfo?.view_mode) {
        state.displayMode = config.extrainfo.view_mode.toLowerCase()
      }
      if (config.extrainfo?.description) {
        state.accessibilityDescription = config.extrainfo.description
      }
      break
    }
    case 'calendar': {
      state.action = 'calendar'
      state.eventTitle = config.extrainfo?.event_title || ''
      state.eventDescription = config.extrainfo?.event_description || ''
      const payload = config.payload
      if (payload) {
        if (payload.includes(',')) {
          // Format: "04/27/2025, 12:17:00,04/30/2025, 12:17:00"
          const parts = payload.split(',')
          if (parts.length >= 4 && parts[0] && parts[1] && parts[2] && parts[3]) {
            state.calendarStart = `${parts[0].trim()}, ${parts[1].trim()}`
            state.calendarEnd = `${parts[2].trim()}, ${parts[3].trim()}`
          }
        } else if (payload.includes('T')) {
          // Format: "2025-04-13T03:52:00.000Z 2025-04-21T03:52:00.000Z"
          const [start, end] = payload.split(' ')
          const startDate = new Date(start)
          const endDate = new Date(end)

          const formatDate = (date: Date): string => {
            const options: Intl.DateTimeFormatOptions = {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }
            return new Intl.DateTimeFormat('en-US', options).format(date)
          }

          state.calendarStart = formatDate(startDate)
          state.calendarEnd = formatDate(endDate)
        }
      }
      break
    }
    case 'location': {
      state.action = 'location'
      state.locationName = config.extrainfo?.label || ''
      state.locationAddress = config.extrainfo?.locationAddress || ''
      if (config.payload) {
        const [latitude, longitude] = config.payload.split(',')
        state.lat = latitude
        state.lng = longitude
      }
      break
    }
    default: {
      // Payload-based detection (when config.type is not explicitly set)
      if (state.payload) {
        if (state.payload.includes('cancel')) {
          state.action = 'cancel'
          state.superAction = 'automation'
        } else if (state.payload.includes('back')) {
          state.action = 'back'
          state.superAction = 'automation'
        } else if (state.payload.includes(AGENT_PREFIX)) {
          state.action = 'agent_or_group'
          state.payload = state.payload.replace(AGENT_PREFIX, '')
          state.superAction = 'livechat'
        } else if (state.payload.includes(LIVECHAT_PAYLOAD)) {
          state.action = 'livechat'
          state.payload = LIVECHAT_PAYLOAD
          state.superAction = 'livechat'
        } else if (state.payload.includes(BOT_PREFIX)) {
          state.action = 'bot_flow'
          state.superAction = 'reply'
          const thisBot = allBots.find((e) => state.payload!.includes(e.id))
          state.payload = thisBot?.id
        } else if (state.payload.includes('http://') || state.payload.includes('https://')) {
          state.action = 'website'
          state.superAction = 'website'
          state.website = state.payload || ''
          state.openurlType = 'browser'
          state.displayMode = 'full'
          state.accessibilityDescription = ''
        } else if (config.action === 'COPY_CODE') {
          state.action = 'COPY_CODE'
        } else if (config.action === 'phone' || config.action === 'phone_number') {
          state.action = 'phone_number'
          state.number = state.payload
        } else if (config.action === 'website' || config.action === 'app_webview') {
          state.action = 'website'
          state.superAction = 'website'
          state.website = state.payload || ''
          if (config.action === 'app_webview') {
            state.openurlType = 'webview'
            if (config.extrainfo?.view_mode) {
              state.displayMode = config.extrainfo.view_mode.toLowerCase()
            }
            if (config.extrainfo?.description) {
              state.accessibilityDescription = config.extrainfo.description
            }
          } else {
            state.openurlType = 'browser'
            state.displayMode = 'full'
            state.accessibilityDescription = ''
          }
        } else if (state.payload === OPT_IN_PAYLOAD) {
          state.action = 'opt_in'
        } else if (state.payload === OPT_OUT_PAYLOAD) {
          state.action = 'opt_out'
        } else {
          state.action = 'custom'
          state.superAction = 'website'
        }
      }
      break
    }
  }

  // If action is phone/phone_number, also set number from payload
  if ((state.action === 'phone' || state.action === 'phone_number') && !state.number) {
    state.number = state.payload
  }

  // If action is website/app_webview and website isn't set yet, derive from payload
  if ((state.action === 'website' || state.action === 'app_webview') && !state.website) {
    state.website = state.payload || ''
  }

  return state
}

// ─── Encode ──────────────────────────────────────────────────────────────────

/**
 * Encodes the current popper editor state into the submit payload format.
 * This is the pure logic extracted from `popper.vue`'s `submit()` method.
 *
 * Returns either `{ legacy, new }` on success, or `{ error }` on validation failure.
 */
export function encodePopperSubmit(input: EncodeInput): EncodeResult {
  let action = input.action
  let payload = input.payload
  let extrainfo: Record<string, any> | undefined = undefined
  let postback_action: string | undefined = undefined

  switch (action) {
    case 'opt_in': {
      payload = OPT_IN_PAYLOAD
      break
    }
    case 'opt_out': {
      payload = OPT_OUT_PAYLOAD
      break
    }
    case 'livechat': {
      payload = LIVECHAT_PAYLOAD
      break
    }
    case 'cancel': {
      payload = 'cancel'
      break
    }
    case 'back': {
      payload = 'back'
      break
    }
    case 'agent_or_group': {
      if (payload?.trim()) {
        postback_action = payload
        payload = AGENT_PREFIX + payload
      } else {
        return { error: 'Please select agent and try again!' }
      }
      break
    }
    case 'bot_flow': {
      if (payload) {
        postback_action = payload
        payload = BOT_PREFIX + payload
      } else {
        return { error: 'Please select bot and try again!' }
      }
      break
    }
    case 'phone_number':
    case 'phone': {
      const isValid = input.validatePhoneNumber(input.number)
      if (isValid === true) {
        payload = input.number
        postback_action = payload
      } else {
        return { error: typeof isValid === 'string' ? isValid : 'Please enter a valid phone number' }
      }
      break
    }
    case 'website':
    case 'app_webview': {
      const isURLValid = input.urlType === 'dynamic' ? true : input.validateUrl(input.website)
      if (isURLValid && input.website?.length) {
        payload = input.website
        postback_action = payload
        if (input.openurlType === 'webview') {
          action = 'app_webview'
          extrainfo = {
            view_mode: input.displayMode.toUpperCase(),
            description: input.accessibilityDescription || '',
          }
        } else {
          action = 'website'
        }
      } else {
        return { error: 'Please enter a valid url' }
      }
      break
    }
    case 'location': {
      if (input.lat && input.lng) {
        payload = `${input.lat},${input.lng}`
        postback_action = payload
        extrainfo = {
          label: input.locationName,
          locationAddress: input.locationAddress,
        }
      } else {
        return { error: 'Please enter latitude and longitude values' }
      }
      break
    }
    case 'calendar': {
      if (!input.eventTitle) {
        return { error: 'Please select event title' }
      }
      if (input.calendarStart && input.calendarEnd) {
        // Validate date range: end must not be before start
        const startDate = new Date(input.calendarStart)
        const endDate = new Date(input.calendarEnd)
        if (endDate < startDate) {
          return { error: 'End date & time cannot be earlier than start date & time.' }
        }
        // Validate start time is not in the past
        const currentDateTime = new Date()
        if (startDate < currentDateTime) {
          return { error: 'Start time cannot be in the past' }
        }
        payload = `${input.calendarStart},${input.calendarEnd}`
        postback_action = payload
        extrainfo = {
          event_title: input.eventTitle,
          event_description: input.eventDescription,
        }
      } else {
        return { error: 'Please select start and end date, time values' }
      }
      break
    }
  }

  // Handle suggestion replies override
  if (input.suggestionButtonType === 'suggestedReply' && input.showSuggestionReplies) {
    action = 'custom'
  }

  // Final validation
  if ((input.text && action && payload) || action === 'share') {
    let submitData: Record<string, any> = {
      text: input.text,
      action,
      payload,
    }
    if (input.description) {
      submitData = { ...submitData, description: input.descriptionText }
    }
    if (action === 'location' || action === 'calendar') {
      submitData = { ...submitData, extrainfo }
    }
    if (action === 'website') {
      submitData = { ...submitData, url_type: input.urlType }
    }
    if (action === 'app_webview') {
      submitData = { ...submitData, url_type: input.urlType, extrainfo }
    }

    // For quick_reply buttons, keep extrainfo.description in sync with the user-edited descriptionText
    // so the PATCH payload always sends the updated description inside extrainfo (not a stale value)
    if (input.configExtrainfo?.quick_reply) {
      const updatedExtrainfo = { ...input.configExtrainfo, description: input.descriptionText }
      submitData = { ...submitData, extrainfo: updatedExtrainfo }
    }

    return {
      legacy: submitData,
      new: {
        ...submitData,
        postback_action_type: action,
        postback_action: postback_action?.toString(),
      },
    }
  }

  return { error: 'Please enter required details' }
}

// ─── Validation ──────────────────────────────────────────────────────────────

/**
 * Validates a dynamic URL pattern (allows {{variable}} placeholders).
 */
export function validateDynamicUrl(url: string): boolean {
  const re = new RegExp(
    '^(?:' +
      '(https?://)([a-zA-Z0-9.-]+)(:[0-9]+)?(/[^?#]*)?(\\?[^#]*)?(#.*)?' +
      '|' +
      '(https?://)([a-zA-Z0-9.-]+)(:[0-9]+)?(/[a-zA-Z0-9.-]+/\\{\\{[^/]+\\}\\})' +
    ')$',
    'i'
  )
  return !!re.test(url)
}
